import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { useAuth } from "@/context/AuthContext";
import { settings } from "@/settings";
import {
  User,
  AvailableUser,
  UserOut,
  RoleOut,
  AzureUser,
  SearchAzureUsersResponse,
  isConsentRequiredError,
} from "@/types/user";
import { SWRApiError, swrConfigOptions, useFetcher } from "./swr";

// Helper function to create a role slug from role name
function createRoleSlug(roleName: string): string {
  return roleName.toLowerCase().replace(/\s+/g, "-");
}

// Helper function to find role by name (case-insensitive)
function findRoleByName(roles: RoleOut[], name: string): RoleOut | undefined {
  return roles.find((role) => role.name.toLowerCase() === name.toLowerCase());
}

// Map frontend role string to role_id
function mapRoleToRoleId(role: string, roles: RoleOut[]): number {
  // Convert role slug back to proper name and find the role
  const roleName = role
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  const roleObj = findRoleByName(roles, roleName);
  if (!roleObj) {
    console.warn(`Unknown role: ${role}, defaulting to contributor (role_id: 2)`);
    return 2; // Default to contributor
  }
  return roleObj.id;
}

// Map API UserOut to frontend User
function mapApiUserToFrontendUser(apiUser: UserOut): User {
  return {
    id: apiUser.entra_oid,
    name: apiUser.name,
    email: apiUser.email,
    role: apiUser.role, // Use role name directly from API
    permissions: apiUser.permissions,
  };
}

// SWR Hooks

export const useUsers = () => {
  const fetcher = useFetcher();

  const { data = [], ...rest } = useSWR<UserOut[], SWRApiError>({ url: "/users" }, fetcher, {
    ...swrConfigOptions,
    suspense: true,
  });

  return {
    data: data.map(mapApiUserToFrontendUser),
    ...rest,
  };
};

export const useUserMe = (accessToken?: string) => {
  const fetcher = useFetcher();

  const { data, ...rest } = useSWR<UserOut, SWRApiError>(
    accessToken ? { url: `/users/me`, accessToken } : null,
    fetcher,
    {
      ...swrConfigOptions,
      shouldRetryOnError: (error) => ![404, 401].includes(error.statusCode),
    },
  );

  return {
    data: data ? mapApiUserToFrontendUser(data) : undefined,
    ...rest,
  };
};

// TODO: these should either be refactored or use the generic fetcher, mutater, updater and deleter swr helpers
// Custom fetcher for search operations that handles consent errors
const searchFetcher = async (params: string[]): Promise<SearchAzureUsersResponse> => {
  const [url, accessToken] = params;

  const response = await fetch(`${settings.apiBaseUrl}${url}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    const error = new Error(errorData.detail || response.statusText) as any;
    error.status = response.status;
    error.response = { status: response.status };
    throw error;
  }

  return response.json();
};

export const useSearchEntraUsers = (query: string) => {
  const { accessToken } = useAuth();

  const { data, error, isLoading } = useSWR<SearchAzureUsersResponse, any>(
    accessToken && query.length >= 2
      ? [`/users/search-azure?query=${encodeURIComponent(query)}&limit=10`, accessToken]
      : null,
    searchFetcher,
    {
      ...swrConfigOptions,
      revalidateOnFocus: false,
      dedupingInterval: 300,
    },
  );

  let availableUsers: AvailableUser[] = [];
  let consentError: string | null = null;
  let consentUrl: string | null = null;

  if (data && !isConsentRequiredError(data)) {
    availableUsers = data.map((azureUser: AzureUser) => ({
      id: azureUser.id,
      name: azureUser.name,
      email: azureUser.email,
      username: azureUser.username,
    }));
  } else if (data && isConsentRequiredError(data)) {
    consentError = data.message;
    consentUrl = data.consent_url;
  }

  return {
    data: availableUsers,
    error: error || consentError,
    consentUrl,
    isLoading,
  };
};

export const useSearchUsers = (query: string) => {
  const { data: allUsers = [], isLoading } = useUsers();

  const filteredUsers = allUsers.filter(
    (user) =>
      user.name.toLowerCase().includes(query.toLowerCase()) ||
      user.email.toLowerCase().includes(query.toLowerCase()),
  );

  return {
    data: filteredUsers,
    isLoading,
  };
};

export const useAddUser = () => {
  const { accessToken } = useAuth();

  return useSWRMutation<UserOut, any, string[] | null, { azureUserId: string; roleId: number }>(
    accessToken ? ["/users", accessToken] : null,
    async (key, { arg }) => {
      const [url, token] = key;

      const response = await fetch(
        `${settings.apiBaseUrl}${url}?azure_user_id=${encodeURIComponent(arg.azureUserId)}&role_id=${arg.roleId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        const error = new Error(errorData.detail || response.statusText) as any;
        error.status = response.status;
        error.response = { status: response.status };
        throw error;
      }

      return response.json();
    },
  );
};

export const useUpdateUserRole = () => {
  const { accessToken } = useAuth();

  return useSWRMutation<UserOut, any, string[] | null, { userId: string; roleId: number }>(
    accessToken ? ["/users", accessToken] : null,
    async (key, { arg }) => {
      const [url, token] = key;

      const response = await fetch(`${settings.apiBaseUrl}${url}/${arg.userId}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ role_id: arg.roleId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const error = new Error(errorData.detail || response.statusText) as any;
        error.status = response.status;
        error.response = { status: response.status };
        throw error;
      }

      return response.json();
    },
  );
};

export const useRemoveUser = () => {
  const { accessToken } = useAuth();

  return useSWRMutation<void, any, string[] | null, { userId: string }>(
    accessToken ? ["/users", accessToken] : null,
    async (key, { arg }) => {
      const [url, token] = key;

      const response = await fetch(`${settings.apiBaseUrl}${url}/${arg.userId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        const error = new Error(errorData.detail || response.statusText) as any;
        error.status = response.status;
        error.response = { status: response.status };
        throw error;
      }
    },
  );
};

// Helper functions for components
export { mapRoleToRoleId, createRoleSlug, mapApiUserToFrontendUser };
